/* muxum-carousel.js — Fase 2 carruseles móvil.
   - Dots accesibles en todos los .mx-mcarousel (solo se ven < 750px vía CSS).
   - Autoplay suave SOLO en [data-mx-autoplay] (testimonios), con guardas.
   - Marquee continuo en .mx-press__row[data-mx-marquee] (todos los viewports).
   Sin librerías. Cargado con `defer`. Progresivo: sin JS los carruseles siguen
   funcionando por swipe y la tira de prensa queda en su wrap estático.
   Patrón IIFE + re-init en shopify:section:load, igual que muxum-reveal.js. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileMQ = window.matchMedia('(max-width: 749px)');

  // Distancia entre el inicio de una tarjeta y la siguiente (ancho + gap).
  function cardStep(cards, container) {
    if (cards.length > 1) {
      const d = cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;
      if (d > 1) return d;
    }
    return container.clientWidth || 1;
  }

  /* ---------- DOTS (todos los .mx-mcarousel) ---------- */
  function initDots(carousel) {
    if (carousel.dataset.mxDots) return;          // idempotente
    const cards = Array.from(carousel.children);
    if (cards.length < 2) return;                 // 0/1 tarjeta → sin dots
    carousel.dataset.mxDots = 'on';

    const nav = document.createElement('div');
    nav.className = 'mx-cdots';
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label', 'Paginación del carrusel');

    cards.forEach((card, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mx-cdots__dot';
      dot.setAttribute('aria-label', 'Ir al elemento ' + (i + 1) + ' de ' + cards.length);
      dot.addEventListener('click', () => {
        const left = card.getBoundingClientRect().left
          - carousel.getBoundingClientRect().left
          + carousel.scrollLeft;
        carousel.scrollTo({ left: left, behavior: 'smooth' });
      });
      nav.appendChild(dot);
    });
    carousel.insertAdjacentElement('afterend', nav);

    const dots = Array.from(nav.children);
    let raf = 0;
    const sync = () => {
      raf = 0;
      const step = cardStep(cards, carousel);
      let idx = Math.round(carousel.scrollLeft / step);
      idx = Math.max(0, Math.min(cards.length - 1, idx));
      dots.forEach((d, i) => {
        const on = i === idx;
        d.classList.toggle('is-active', on);
        if (on) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    };
    carousel.addEventListener('scroll', () => {
      if (!raf) raf = requestAnimationFrame(sync);
    }, { passive: true });
    sync();
  }

  /* ---------- AUTOPLAY (solo [data-mx-autoplay] = testimonios) ---------- */
  function initAutoplay(carousel) {
    if (carousel.dataset.mxAutoplayInit) return;
    if (reduceMotion.matches) return;             // respeta prefers-reduced-motion
    const cards = Array.from(carousel.children);
    if (cards.length < 2) return;
    carousel.dataset.mxAutoplayInit = 'on';

    const speed = parseInt(carousel.dataset.mxAutoplaySpeed || '6000', 10);
    let inView = false, paused = false, touching = false, touchTimer = 0;

    // Solo avanza en móvil, en viewport, pestaña visible y sin interacción.
    const canRun = () => mobileMQ.matches && inView && !paused && !touching
      && !document.hidden && !reduceMotion.matches;

    // Avanza al INICIO de la siguiente tarjeta (robusto con anchos desiguales,
    // p.ej. las stats del Hero); al llegar al final, vuelve a 0 (loop).
    const tick = () => {
      if (!canRun()) return;
      const max = carousel.scrollWidth - carousel.clientWidth;
      const cur = carousel.scrollLeft;
      // si ya estamos (casi) al final, loop directo — evita un tick "casi-nulo"
      // que dejaba el último par mostrándose el doble de tiempo.
      if (cur >= max - 8) { carousel.scrollTo({ left: 0, behavior: 'smooth' }); return; }
      const base = carousel.getBoundingClientRect().left;
      for (let i = 0; i < cards.length; i++) {
        const left = cards[i].getBoundingClientRect().left - base + cur;
        if (left > cur + 4) { carousel.scrollTo({ left: left, behavior: 'smooth' }); return; }
      }
      carousel.scrollTo({ left: 0, behavior: 'smooth' });
    };
    setInterval(tick, speed);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        inView = entries[0].isIntersecting;
      }, { threshold: 0.4 }).observe(carousel);
    } else {
      inView = true;
    }

    // Pausa en hover / focus / touch (con cortesía de 4s tras soltar).
    carousel.addEventListener('mouseenter', () => { paused = true; });
    carousel.addEventListener('mouseleave', () => { paused = false; });
    carousel.addEventListener('focusin', () => { paused = true; });
    carousel.addEventListener('focusout', () => { paused = false; });
    carousel.addEventListener('touchstart', () => {
      touching = true; clearTimeout(touchTimer);
    }, { passive: true });
    carousel.addEventListener('touchend', () => {
      clearTimeout(touchTimer);
      touchTimer = setTimeout(() => { touching = false; }, 4000);
    }, { passive: true });
  }

  /* ---------- MARQUEE (solo .mx-press__row[data-mx-marquee]) ---------- */
  function initMarquee(row) {
    if (row.dataset.mxMarquee === 'on') return;
    if (reduceMotion.matches) return;             // queda el wrap estático actual
    const items = Array.from(row.children);
    if (!items.length) return;
    row.dataset.mxMarquee = 'on';

    const track = document.createElement('div');
    track.className = 'mx-press__track';
    items.forEach((it) => track.appendChild(it));            // mover originales
    items.forEach((it) => {                                  // clonar (2 copias)
      const clone = it.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
    row.appendChild(track);
    row.classList.add('mx-press__row--marquee');

    // Mide una copia y fija desplazamiento + duración (velocidad ~40px/s, calma).
    // Se re-mide tras cargar fuentes (Gopher cambia anchos) para que el empalme no salte.
    const setShift = () => {
      const copyW = track.scrollWidth / 2;
      if (copyW > 0) {
        track.style.setProperty('--mx-marquee-shift', copyW + 'px');
        track.style.setProperty('--mx-marquee-dur', Math.max(12, Math.round(copyW / 40)) + 's');
      }
    };
    requestAnimationFrame(setShift);
    window.addEventListener('load', setShift);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(setShift);

    // Pausa táctil (hover/focus se manejan por CSS).
    row.addEventListener('touchstart', () => row.classList.add('is-paused'), { passive: true });
    row.addEventListener('touchend', () => row.classList.remove('is-paused'), { passive: true });
  }

  /* ---------- bootstrap ---------- */
  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll('.mx-mcarousel').forEach(initDots);
    // Autoplay: cualquier carrusel con [data-mx-autoplay] (testimonios .mx-mcarousel
    // y la tira de stats del Hero, que NO es .mx-mcarousel). Mobile-only (canRun).
    root.querySelectorAll('[data-mx-autoplay]').forEach(initAutoplay);
    root.querySelectorAll('.mx-press__row[data-mx-marquee]').forEach(initMarquee);
  }

  initAll(document);
  document.addEventListener('shopify:section:load', () => initAll(document));
})();
